import AdministrationAction from 'administration/actions/AdministrationAction'
import Select from 'components/forms/Select'
import { sortBy, uniqBy } from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import Input from 'components/forms/Input'
import { Button, Card, CardContent, Grid } from '@mui/material'
import { normalizeString } from 'utils/StringUtil'
import PropTypes from 'prop-types'
import SettingDto from 'administration/dto/SettingDto'
import { NewTable } from 'components/datatable/NewTable'
import useActions from 'utils/customHook/useActions'
import PopupAddParameter from 'administration/components/parameter/PopupAddParameter'

const headers = ['parameter', 'value', 'updateDate']

const Filter = ({
    filterParent,
    parameters,
    changeFilterParent,
}) => {
    const [filter, setFilter] = useState(filterParent)
    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} justifyContent={'space-between'} alignItems='center'>
                    <Grid size={4}>
                        <Input
                            title={ i18n.search }
                            value={ filter.searchValue }
                            onChange={ v => setFilter(prev => ({ ...prev, searchValue: v })) }
                            onEnterKeyPress={ v => changeFilterParent({ ...filter, searchValue: v }) }
                            data-cy={'search_input'}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={ i18n.parameters }
                            value={ filter.param }
                            options={ parameters }
                            onChange={ v => setFilter(prev => ({ ...prev, param: v })) }
                            returnNull={ true }
                            keyValue='parameter'
                            keyLabel='parameter'
                        />
                    </Grid>
                    <Grid size={4} spacing={1} container justifyContent='flex-end'>
                        <Grid>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    setFilter({})
                                    changeFilterParent({})
                                }}
                                data-cy={'reset_button'}
                            >
                                {i18n.reinit}
                            </Button>
                        </Grid>
                        <Grid>
                            <Button
                                variant='contained'
                                onClick={() => changeFilterParent(filter)}
                                data-cy={'search_button'}
                            >
                                {i18n.search}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
Filter.propTypes = {
    filterParent: PropTypes.shape({
        searchValue: PropTypes.string,
    }),
    parameters: PropTypes.arrayOf(PropTypes.instanceOf(SettingDto)),
    changeFilterParent: PropTypes.func,
}

const UserParameterPanel = () => {
    const {
        userSettings,
        user,
    } = useSelector(store => ({
        userSettings: store.AdministrationReducer.userSettings,
        user: store.UserReducer.user,
    }), shallowEqual)

    const [editMode, setEditMode] = useState(false)
    const [openAddModale, setOpenAddModale] = useState(false)
    const [openEditModale, setOpenEditModale] = useState(false)
    const [parameter, setParameter] = useState({})
    const [parameters, setParameters] = useState([])
    const [filter, setFilter] = useState({})

    const dispatch = useDispatch()

    const exportParameters = () => {
        const parametersToExport = parameters.map((param, i) => {
            return i === 0 ? { headers, ...param } : param
        })
        return {
            data: parametersToExport,
            exportType: 'xlsx',
            titleFile: `${i18n.parameters}_${user.login}`,
        }
    }

    useEffect(() => {
        dispatch(AdministrationAction.fetchUserSettings(user.login))
    }, [user])

    useEffect(() => {
        // filter because settings with empty login are fetched too
        setParameters(userSettings.filter(param => param.login === user.login).map(param => ({
            ...param,
            updateDate: param.updateDate ? moment(param.updateDate).format('DD/MM/YYYY') : '',
        })))
    }, [userSettings])

    useActions(() => {
        if (editMode) {
            return {
                new: () => setOpenAddModale(true),
                cancel: () => setEditMode(false),
                export: () => exportParameters(),
            }
        }
        return {
            edit: () => setEditMode(true),
            export: () => exportParameters(),
        }
    }, [editMode])

    const onDelete = (deleteParam) => {
        dispatch(AdministrationAction.deleteUserSetting({
            parameter: deleteParam.parameter,
            value: deleteParam.value,
            login: user.login,
        }))
    }

    const onAdd = (addParam) => {
        dispatch(AdministrationAction.createUserSetting({
            parameter: addParam.parameter,
            value: addParam.value,
            login: user.login,
        }))
        setOpenAddModale(false)
    }

    const onEdit = (editParameter) => {
        setParameter(editParameter)
        setOpenEditModale(true)
    }

    const onUpdate = (updateParam) => {
        const param = {
            parameter: updateParam.parameter,
            value: updateParam.value,
            login: user.login,
            updateDate: moment().valueOf(),
        }
        dispatch(AdministrationAction.updateUserSetting(param))
        setOpenEditModale(false)
    }

    const optionsParameters = sortBy(uniqBy(parameters, 'parameter'), 'parameter')

    const parametersFiltered = useMemo(() => {
        const filterSearch = filter.searchValue ? parameters.filter(p => headers.some((k) => normalizeString(p[k])?.includes(normalizeString(filter.searchValue)))) : parameters
        return filter.param ? filterSearch.filter(p => p.parameter === filter.param) : filterSearch
    }, [parameters, filter])

    return (
        <Grid container spacing={1}>
            <Grid size={12}>
                <Filter
                    filterParent={ filter }
                    parameters={ optionsParameters }
                    changeFilterParent={v => setFilter(v)}
                />
            </Grid>
            <Grid size={12}>
                <NewTable
                    headers={headers}
                    rowsPerPageOptions={nbPerPageLabelMedium}
                    rows={parametersFiltered}
                    data-cy={'parameter_table'}
                    lineActions={[
                        { icon: 'delete', onClick: onDelete, displayed: editMode },
                        { icon: 'edit', onClick: onEdit, displayed: editMode },
                    ]}
                />
            </Grid>
            {openAddModale && (
                <PopupAddParameter
                    openDialog={openAddModale}
                    addParameter={onAdd}
                    closeDialog={() => setOpenAddModale(false)}
                />
            )}
            { openEditModale && (
                <PopupAddParameter
                    parameter={ parameter }
                    openDialog={openEditModale}
                    addParameter={onUpdate}
                    closeDialog={() => setOpenEditModale(false)}
                />
            )}
        </Grid>

    )
}

export default UserParameterPanel
